<template>
    <div class="w-full">
        <div class="text-slate-700 text-base font-bold mb-2">API Auth</div>

        <div v-if="emptyAuthConfigs">
            <vx-popover hover
                class="w-full"
                :arrow="true"
                placement="top">
                <vx-button block
                    :disabled="!isAdminOrOwner"
                    @click.prevent="addNewAuthConfig()"
                    type="button"
                    color="muted"
                    size="lg">Add New <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon></vx-button>
                <template v-if="!isAdminOrOwner"
                    v-slot:content>
                    You do not have permission to create.
                </template>
            </vx-popover>
        </div>
        <div v-else
            class="flex flex-col gap-2 lg:flex-row">
            <vx-selectable size="lg"
                placeholder="Select an API Auth"
                :modelValue="props.modelValue"
                @update:modelValue="emit('update:modelValue', $event)"
                :error="props.error"
                :options="authConfigs"
                :reduce="option => option.auth_config_id"
                label="friendly_name"></vx-selectable>
            <vx-popover hover
                class="w-full lg:w-auto"
                :arrow="true"
                placement="top">
                <vx-button block
                    :disabled="!isAdminOrOwner"
                    @click.prevent="addNewAuthConfig()"
                    type="button"
                    color="muted"
                    size="lg">Add New <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon></vx-button>
                <template v-if="!isAdminOrOwner"
                    v-slot:content>
                    You do not have permission to create.
                </template>
            </vx-popover>
        </div>

        <auth-config-type-modal v-model:visible="showAuthConfigTypeModal"
            @continue="authConfigType = $event"></auth-config-type-modal>
        <auth-config-modal-form :visible="!!authConfigType"
            @update:visible="authConfigType = undefined"
            :authConfigId="props.modelValue"
            :authConfigType="authConfigType"
            @upserted="upsertAndSelect($event)"></auth-config-modal-form>
    </div>
</template>

<script setup>
import {
    VxSelectable,
    VxPopover,
    VxButton,
} from '@voxie/frontend-components';
import { onMounted, ref, watch } from "vue";
import { authConfigsClient } from '~/services'
import { useToasted } from "../../../../../composables/useToasted";
import { uniqBy } from "~/utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import AuthConfigModalForm from "../../auth-configs/form/AuthConfigModalForm.vue";
import AuthConfigTypeModal from "~/components/settings/integrations/auth-configs/AuthConfigTypeModal.vue";

const props = defineProps({
    modelValue: {
        type: String,
        required: false,
        default: undefined,
    },
    error: {
        type: String,
        default: undefined,
    },
    authModalVisible: {
        type: Boolean,
        default: false,
    }
});

const isAdminOrOwner = Spark.isAdminOrOwner;
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();
const emit = defineEmits(['update:modelValue', 'update:authModalVisible']);

const authConfigType = ref()
const showAuthConfigTypeModal = ref(false);

const loading = ref(false);
const emptyAuthConfigs = ref(false);
const authConfigs = ref([]);

const addNewAuthConfig = () => {
    showAuthConfigTypeModal.value = true;
    emit('update:modelValue', null);
}

watch(() => showAuthConfigTypeModal.value || authConfigType.value, (visible) => {
    emit('update:authModalVisible', visible)
});

const upsertAndSelect = (authConfig) => {
    emptyAuthConfigs.value = false;
    toasted.success('API Auth saved and selected')

    authConfigs.value.unshift(authConfig);
    authConfigs.value = uniqBy(authConfigs.value, 'auth_config_id');

    emit('update:modelValue', authConfig.auth_config_id);
}

onMounted(async () => {
    loading.value = true;

    if (props.modelValue) {
        try {
            const authConfig = (await authConfigsClient.get(teamId, props.modelValue)).data;
            authConfigs.value = [authConfig]
        } catch (e) {
            console.error(e);
            toasted.global.platform_error()
        }
    }

    try {
        authConfigs.value = authConfigs.value.concat((await authConfigsClient.list(teamId, {
            limit: 50,
            forward: false,
        })).data.data)
        authConfigs.value = uniqBy(authConfigs.value, 'auth_config_id');
        emptyAuthConfigs.value = !authConfigs.value.length;
    } catch (e) {
        console.error(e)
        toasted.global.platform_error();
    } finally {
        loading.value = false
    }
});

</script>
